import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Upload, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProgressBar from "./ProgressBar";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/api";

const PartnerRegistration = () => {
  const navigate = useNavigate();
  const [selectedDays, setSelectedDays] = useState([]);

  const [currentStep, setCurrentStep] = useState(0); // Start from 0 for phone verification
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);

  // Phone verification states
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [smsOtp, setSmsOtp] = useState(["", "", "", "", "", ""]);
  const [smsOtpSent, setSmsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneForVerification, setPhoneForVerification] = useState("");

  const [formData, setFormData] = useState({
    businessName: "",
    ownerName: "",
    email: "",
    phone: "",
    bank_name: "",
    branch_name: "",
    ifsc_code: "",
    account_number: "",
    category: "restaurant",
    address: "",
    city: "",
    pincode: "",
    password: "",
    confirmPassword: "",
    openingTime: "",
    closingTime: "",
    image_url: [],
    menu_image_url: [],
    logo_url: "",
    location_id: "",
    map_url: "",
    latitude: "",
    longitude: "",
  });

  // Map link extraction states
  const [mapLinkInput, setMapLinkInput] = useState("");
  const [extractedCoords, setExtractedCoords] = useState(null);
  const [isExtractingCoords, setIsExtractingCoords] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    api.get('/location/dropdown/list')
      .then((response) => {
        console.log("data for the dropdown locations : ", response.data);
        setLocations(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
        toast.error("Failed to load locations");
      });
  }, []);

  useEffect(() => {
    const filtered = locations.filter((location) =>
      location.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLocations(filtered);
  }, [searchTerm, locations]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDayToggle = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Function to extract latitude and longitude from Google Maps URL
  const extractCoordinatesFromMapUrl = (url) => {
    try {
      // Pattern 1: https://www.google.com/maps/search/hotel+trichy+sangeethas/@10.8158551,78.6755113,14z/
      const pattern1 = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
      const match1 = url.match(pattern1);
      if (match1) {
        return {
          latitude: parseFloat(match1[1]),
          longitude: parseFloat(match1[2])
        };
      }

      // Pattern 2: https://maps.app.goo.gl/wdqQEMRvELS7Cz7F6 (shortened URL)
      // For shortened URLs, we'll need to expand them or use a different approach
      if (url.includes('maps.app.goo.gl') || url.includes('goo.gl')) {
        toast.info('Please use the full Google Maps URL with coordinates visible');
        return null;
      }

      // Pattern 3: Other Google Maps formats
      const pattern3 = /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/;
      const match3 = url.match(pattern3);
      if (match3) {
        return {
          latitude: parseFloat(match3[1]),
          longitude: parseFloat(match3[2])
        };
      }

      // Pattern 4: Another common format
      const pattern4 = /\/maps\/@(-?\d+\.\d+),(-?\d+\.\d+)/;
      const match4 = url.match(pattern4);
      if (match4) {
        return {
          latitude: parseFloat(match4[1]),
          longitude: parseFloat(match4[2])
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  // Handle map link extraction
  const handleMapLinkExtraction = () => {
    if (!mapLinkInput.trim()) {
      toast.error('Please enter a Google Maps link');
      return;
    }

    setIsExtractingCoords(true);

    // Extract coordinates from the URL
    const coords = extractCoordinatesFromMapUrl(mapLinkInput);

    if (coords) {
      setExtractedCoords(coords);
      setFormData(prev => ({
        ...prev,
        map_url: mapLinkInput,
        latitude: coords.latitude.toString(),
        longitude: coords.longitude.toString()
      }));
      toast.success(`Coordinates extracted successfully! Lat: ${coords.latitude}, Lng: ${coords.longitude}`);
    } else {
      toast.error('Could not extract coordinates from this link. Please check the URL format.');
      setExtractedCoords(null);
    }

    setIsExtractingCoords(false);
  };

  // Reset map link data
  const resetMapLink = () => {
    setMapLinkInput('');
    setExtractedCoords(null);
    setFormData(prev => ({
      ...prev,
      map_url: '',
      latitude: '',
      longitude: ''
    }));
  };

  // SMS OTP handling functions
  const handleSmsOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...smsOtp];
      newOtp[index] = value;
      setSmsOtp(newOtp);

      // Auto-focus next input
      if (value !== "" && index < 5) {
        const nextInput = document.querySelector(
          `input[name='sms-otp-${index + 1}']`
        );
        nextInput?.focus();
      }
    }
  };

  // Function to send OTP to SMS
  const handleSmsLogin = async () => {
    if (!phoneForVerification) {
      toast.error("Please enter your phone number");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(
        "/auth/sendSmsOtpSignup",
        { phone_number: phoneForVerification }
      );

      if (response.data.message === "OTP sent successfully") {
        setSmsOtpSent(true);
        toast.success("OTP sent to your phone successfully!");
      }
    } catch (error) {
      console.error("Error sending SMS OTP:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send OTP. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySmsOtp = async () => {
    const otpString = smsOtp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(
        "/auth/verifyOtpforPhone",
        {
          phone_number: phoneForVerification,
          otp: otpString,
          isAgent: false,
        }
      );

      const data = response.data;
      if (data.message === "OTP verified successfully" && data.verified === true) {
        setIsPhoneVerified(true);
        // Set the verified phone number in formData
        setFormData((prev) => ({
          ...prev,
          phone: phoneForVerification,
        }));
        setCurrentStep(1); // Move to next step
        toast.success("Phone number verified successfully!");
      }
    } catch (error) {
      console.error("Error verifying SMS OTP:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to verify OTP. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendSmsOtp = async () => {
    setIsLoading(true);
    try {
      await api.post(
        "/auth/sendSmsOtpSignup",
        { phone_number: phoneForVerification }
      );
      toast.success("OTP resent to your phone!");
      setSmsOtp(["", "", "", "", "", ""]);
    } catch (error) {
      console.error("Error resending SMS OTP:", error);
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) {
      console.error("No file provided");
      toast.error("No file selected");
      return null;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(
        "/upload/single",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      return response.data.fileUrl;
    } catch (error) {
      console.error("Error uploading image:", error);

      // Handle specific error messages from backend
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Check for specific error types based on our S3 middleware
        if (errorData.error === "File too large") {
          toast.error(errorData.message || "File size should not exceed 5MB maximum");
        } else if (errorData.error === "Invalid file type") {
          toast.error(errorData.message || "Only images and videos are allowed");
        } else if (errorData.error === "No file uploaded") {
          toast.error("Please select a file to upload");
        } else if (errorData.message) {
          // Show any other specific error message from backend
          toast.error(errorData.message);
        } else if (errorData.error) {
          toast.error(errorData.error);
        } else {
          toast.error("File upload failed. Please try again.");
        }
      } else if (error.response && error.response.status) {
        // Handle HTTP status code errors
        if (error.response.status === 413 ||
          (error.response.data && error.response.data.includes && error.response.data.includes("Request Entity Too Large"))) {
          toast.error("File too large. Maximum size allowed is 5MB.");
        }
        else {
          switch (error.response.status) {
            case 400:
              toast.error("Bad request. Please check your file and try again.");
              break;
            case 401:
              toast.error("Authentication required. Please login again.");
              break;
            case 413:
              toast.error("File too large. Maximum size allowed is 5MB.");
              break;
            case 500:
              toast.error("Server error. Please try again later.");
              break;
            default:
              toast.error(`Upload failed with status: ${error.response.status}`);
          }
        }
      } else if (error.request) {
        // Network error
        console.error("Network error:", error.request);
        toast.error("Network error. Please check your connection and try again.");
      } else {
        // Other errors
        toast.error("An unexpected error occurred during upload.");
      }

      return null;
    }
  };
  const [imageUploading, setImageUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [menuImageUploading, setMenuImageUploading] = useState(false);

  const handleImageUpload = async (files) => {
    setImageUploading(true);
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      setFormData((prev) => ({
        ...prev,
        image_url: [...prev.image_url, ...urls.filter((url) => url !== null)],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload some images");
    }
    finally {
      setImageUploading(false);
    }
  };

  const handleMenuImageUpload = async (files) => {
    setMenuImageUploading(true);
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      setFormData((prev) => ({
        ...prev,
        menu_image_url: [...prev.menu_image_url, ...urls.filter((url) => url !== null)],
      }));
    } catch (error) {
      console.error("Error uploading menu images:", error);
      toast.error("Failed to upload some menu images");
    }
    finally {
      setMenuImageUploading(false);
    }
  };


  const handleLogoUpload = async (file) => {
    if (!file) {
      console.error("File is undefined");
      toast.error("Please select a file before uploading.");
      return;
    }
    setLogoUploading(true);
    const url = await uploadImage(file);
    if (url) {
      setFormData((prev) => ({
        ...prev,
        logo_url: url,
      }));
    }
    setLogoUploading(false);
  };

  const handleContinue = () => {
    if (currentStep === 0) {
      // Phone verification step
      if (!isPhoneVerified) {
        toast.error("Please verify your phone number first");
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      // Step 1 validation - all fields mandatory
      if (
        !formData.businessName ||
        !formData.ownerName ||
        !formData.email ||
        !formData.phone ||
        !formData.category ||
        !formData.address ||
        !formData.city ||
        !formData.pincode ||
        !formData.location_id ||
        !formData.map_url ||
        !formData.latitude ||
        !formData.longitude
      ) {
        toast.error("Please fill all the required fields");
        return;
      }
      //   if (!formData.email.includes('@')) {
      //   toast.error("Please enter a valid email address");
      //   return;
      // }

      // Additional email validation for better format checking
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address format");
        return;
      }

      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2 validation - business hours mandatory (images now optional)
      if (selectedDays.length === 0) {
        toast.error("Please select at least one business day");
        return;
      }
      if (!formData.openingTime || !formData.closingTime) {
        toast.error("Please set opening and closing times");
        return;
      }
      if (formData.bank_name && !/^[A-Za-z\s]+$/.test(formData.bank_name)) {
        toast.error("Bank name should only contain letters");
        return;
      }
      // IFSC validation: exactly 11 characters
      if (formData.ifsc_code && formData.ifsc_code.length !== 11) {
        toast.error("IFSC code must be exactly 11 characters");
        return;
      }
      // Account Number validation: only numbers
      if (formData.account_number && !/^\d+$/.test(formData.account_number)) {
        toast.error("Account number should contain only numbers");
        return;
      }

      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Step 3 validation - passwords mandatory
      if (!formData.password || !formData.confirmPassword) {
        toast.error("Please fill in both password fields");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords don't match");
        return;
      }
      if (formData.password.length < 6) {
        toast.error("Password must be at least 6 characters long");
        return;
      }
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const submitData = {
      name: formData.ownerName,
      email: formData.email,
      phone_number: formData.phone,
      password: formData.password,
      account_number: formData.account_number,
      ifsc_code: formData.ifsc_code,
      bank_name: formData.bank_name,
      branch_name: formData.branch_name,
      business_name: formData.businessName,
      owner_name: formData.ownerName,
      image_url: formData.image_url,
      menu_image_url: formData.menu_image_url,
      logo_url: formData.logo_url,
      address: formData.address,
      city: formData.city,
      pincode: formData.pincode,
      businessHours: {
        days: selectedDays,
        openingTime: formData.openingTime,
        closingTime: formData.closingTime,
      },
      location_id: formData.location_id,
      map_url: formData.map_url,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
    };

    // Debug logging
    console.log('Form Data before submission:', formData);
    console.log('Submit Data with coordinates:', {
      map_url: submitData.map_url,
      latitude: submitData.latitude,
      longitude: submitData.longitude,
      formData_latitude: formData.latitude,
      formData_longitude: formData.longitude
    });

    api
      .post(
        `/${formData.category}`,
        submitData
      )
      .then((response) => {
        if (response.status === 201) {
          const successMessage = formData.category === 'activities'
            ? "Registration submitted successfully! Your Activity partner account is pending admin approval. You will be able to login once approved."
            : "Registration successful! You can login once admin has approved it.";

          toast.success(successMessage,
            {
              autoClose: 5000,
              pauseOnHover: true,
              closeOnClick: true,
            }
          );

          setTimeout(() => {
            navigate("/login");
          }, 5500);
        } else {
          toast.error(response.data.message || "Submission failed. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Final Submission Error Detail:", {
          message: error.message,
          code: error.code,
          response: error.response?.data,
          status: error.response?.status
        });

        if (error.code === 'ECONNABORTED') {
          toast.error("Submission timed out. Please check your internet and try again.");
        } else if (error.message === 'Network Error') {
          toast.error("Network error. This might be due to a poor connection or browser restriction.");
        } else if (error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("An unexpected error occurred during final submission. Please check the console for details.");
        }
      });
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLocationSelect = (location) => {
    setFormData((prev) => ({
      ...prev,
      location_id: location._id,
    }));
    setIsLocationDropdownOpen(false);
  };

  const renderLocationDropdown = () => (
    <div className="relative location-dropdown space-y-1.5">
      <label className="text-xs font-semibold text-gray-800 ml-1">
        Location <span className="text-red-500">*</span>
      </label>
      <div
        className="w-full px-3 py-2.5 rounded-lg bg-white/60 border border-gray-200 cursor-pointer flex justify-between items-center transition-all focus-within:ring-2 focus-within:ring-[#067978]"
        onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
      >
        <span
          className={`text-sm font-medium ${!formData.location_id ? "text-gray-400" : "text-gray-900"
            }`}
        >
          {formData.location_id
            ? locations.find((l) => l._id === formData.location_id)?.name
            : "Select a location"}
        </span>
        <svg
          className={`w-4 h-4 text-[#067978] transition-transform duration-300 ${isLocationDropdownOpen ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isLocationDropdownOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full pl-9 pr-4 py-2 text-sm rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#067978]/20 transition-all font-medium text-gray-700"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto scroll-custom">
            {filteredLocations.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                No locations found
              </div>
            ) : (
              filteredLocations.map((location) => (
                <div
                  key={location._id}
                  className="px-4 py-3 hover:bg-[#067978]/5 cursor-pointer flex items-center justify-between border-b border-gray-50 last:border-0 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLocationSelect(location);
                  }}
                >
                  <span className="text-sm font-medium text-gray-700">{location.name}</span>
                  {location.type && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#067978]/10 text-[#067978] px-2 py-1 rounded-md">
                      {location.type}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );

  /* Phone verification step (Step 0) - Refactored to match Agent Style */
  const renderPhoneVerificationStep = () => (
    <div>
      {!smsOtpSent ? (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-800 ml-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="bg-white/60 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center">
              <span className="text-[#067978] font-bold mr-3 text-base">+91</span>
              <input
                type="tel"
                className="w-full bg-transparent text-gray-900 font-medium text-base outline-none placeholder:text-gray-400"
                value={phoneForVerification}
                onChange={(e) => setPhoneForVerification(e.target.value)}
                placeholder="9876543210"
                maxLength="10"
                required
              />
            </div>
          </div>

          <button
            onClick={handleSmsLogin}
            disabled={isLoading}
            className="w-full py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            {isLoading ? "Sending Code..." : "Send Verification Code"}
          </button>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 font-medium">
              Have an account already?{" "}
              <span
                onClick={() => navigate("/login")}
                className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
              >
                Login
              </span>
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-800 ml-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="bg-gray-100 border border-gray-200 rounded-xl px-4 py-2.5 flex items-center">
              <span className="text-gray-500 font-bold mr-3 text-base">+91</span>
              <input
                type="tel"
                className="w-full bg-transparent text-gray-800 font-semibold text-base outline-none"
                value={phoneForVerification}
                readOnly
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-col items-center gap-1">
              <label className="text-sm font-bold text-gray-900">
                Enter Verification Code
              </label>
              <p className="text-gray-500 text-xs">6-digit code sent to your phone</p>
            </div>

            <div className="flex gap-2 justify-center">
              {smsOtp.map((digit, index) => (
                <input
                  key={index}
                  type="tel"
                  name={`sms-otp-${index}`}
                  className="w-10 h-10 md:w-12 md:h-12 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleSmsOtpChange(index, e.target.value)}
                  required
                />
              ))}
            </div>

            <button
              onClick={handleResendSmsOtp}
              disabled={isLoading}
              className="w-full text-center text-xs text-[#067978] hover:underline font-semibold"
            >
              Didn't receive code? Resend
            </button>
          </div>

          <div className="flex flex-col gap-2.5 pt-2">
            <button
              onClick={handleVerifySmsOtp}
              disabled={isLoading}
              className="w-full bg-[#067978] hover:bg-[#05605f] text-white rounded-xl py-3 text-base font-bold shadow-lg transition-all disabled:opacity-70 cursor-pointer"
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </button>
            <button
              onClick={() => {
                setSmsOtpSent(false);
                setSmsOtp(["", "", "", "", "", ""]);
              }}
              disabled={isLoading}
              className="w-full bg-white border border-gray-300 text-gray-700 rounded-xl py-2.5 text-xs font-semibold hover:bg-gray-50 transition-all cursor-pointer"
            >
              Edit Phone Number
            </button>
          </div>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 font-medium">
              Have an account already?{" "}
              <span
                onClick={() => navigate("/login")}
                className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
              >
                Login
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderStepOne = () => (
    <div className="space-y-3.5 animate-fade-in-up">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="businessName"
            placeholder="Enter business name"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
            value={formData.businessName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            Owner Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="ownerName"
            placeholder="Enter owner name"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
            value={formData.ownerName}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-800 ml-1">
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          placeholder="Enter email address"
          className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
          value={formData.email}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            Verified Phone
          </label>
          <div className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5 flex items-center">
            <span className="text-gray-500 font-bold mr-2 text-sm">+91</span>
            <input
              type="tel"
              className="w-full bg-transparent text-gray-700 font-semibold text-sm outline-none"
              value={formData.phone || phoneForVerification}
              readOnly
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            Category <span className="text-red-500">*</span>
          </label>
          <div className="bg-white/60 border border-gray-200 rounded-lg px-3 py-2">
            <select
              name="category"
              className="w-full bg-transparent text-gray-900 font-medium text-sm outline-none cursor-pointer"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="restaurant">Restaurant</option>
              <option value="shop">Shop</option>
              <option value="activities">Activities</option>
            </select>
          </div>
        </div>
      </div>

      {renderLocationDropdown()}

      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-800 ml-1">
          Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="address"
          placeholder="Enter complete address"
          className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
          value={formData.address}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="city"
            placeholder="City"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
            value={formData.city}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-800 ml-1">
            Pincode <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="pincode"
            placeholder="Pincode"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
            value={formData.pincode}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      {/* Google Maps URL Section */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-800 ml-1">
          Google Maps Link <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          <input
            type="url"
            placeholder="https://maps.google.com/..."
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
            value={mapLinkInput}
            onChange={(e) => setMapLinkInput(e.target.value)}
            required
          />
          <button
            type="button"
            onClick={handleMapLinkExtraction}
            disabled={isExtractingCoords || !mapLinkInput.trim()}
            className="w-full bg-[#067978] text-white rounded-lg py-2.5 text-xs font-bold hover:bg-[#05605f] disabled:opacity-50 transition-all cursor-pointer"
          >
            {isExtractingCoords ? 'Extracting Coordinates...' : 'Get Coordinates'}
          </button>

          {extractedCoords && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
              <h4 className="text-xs font-bold text-emerald-800 mb-1">✓ Coordinates Extracted!</h4>
              <div className="text-xs text-emerald-700 space-y-0.5">
                <p><strong>Lat:</strong> {extractedCoords.latitude} | <strong>Lng:</strong> {extractedCoords.longitude}</p>
              </div>
              <button
                type="button"
                onClick={resetMapLink}
                className="mt-1 text-[11px] text-red-600 hover:underline"
              >
                Reset Link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="space-y-4 animate-fade-in-up">
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-0.5">
            Business Logo <span className="text-gray-400 text-xs font-normal ml-1">(Optional)</span>
          </h3>
          <p className="text-xs text-gray-500 mb-2">Recommended Size 200x200px</p>

          <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-gray-50 transition-all cursor-pointer group">
            <input
              type="file"
              className="hidden"
              id="logoUpload"
              onChange={(e) => handleLogoUpload(e.target.files[0])}
              accept="image/*"
            />
            <label
              htmlFor="logoUpload"
              className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
            >
              <Upload className="w-6 h-6 text-gray-400 mb-1 group-hover:text-[#067978] transition-colors" />
              <span className="text-xs font-semibold text-gray-700 group-hover:text-[#067978]">Click to Upload Logo</span>
            </label>
          </div>

          {logoUploading && (
            <div className="flex items-center justify-center mt-2">
              <span className="text-[#067978] text-xs font-bold mr-2 uppercase tracking-wide">Uploading...</span>
              <div className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {formData.logo_url && (
            <div className="mt-2 flex justify-center">
              <div className="p-1 bg-white border border-gray-200 rounded-lg shadow-sm">
                <img
                  src={formData.logo_url}
                  alt="Business Logo"
                  className="w-20 h-20 object-cover rounded"
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-0.5">
            {formData.category} Images <span className="text-gray-400 text-xs font-normal ml-1">(Optional)</span>
          </h3>
          <p className="text-xs text-gray-500 mb-2">Recommended Size 300x300px</p>

          <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-gray-50 transition-all cursor-pointer group">
            <input
              type="file"
              multiple
              className="hidden"
              id="imageUpload"
              onChange={(e) => handleImageUpload(e.target.files)}
              accept="image/*"
            />
            <label
              htmlFor="imageUpload"
              className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
            >
              <Upload className="w-6 h-6 text-gray-400 mb-1 group-hover:text-[#067978] transition-colors" />
              <span className="text-xs font-semibold text-gray-700 group-hover:text-[#067978]">Click to Upload Images</span>
            </label>

            {imageUploading && (
              <div className="flex items-center justify-center mt-2">
                <span className="text-[#067978] text-xs font-bold mr-2 uppercase tracking-wide">Uploading...</span>
                <div className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {formData.image_url.length > 0 && (
              <div className="mt-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase mb-2 text-left">Uploaded ({formData.image_url.length})</h4>
                <div className="grid grid-cols-3 gap-2">
                  {formData.image_url.map((image, index) => (
                    <div key={index} className="relative group/img aspect-square">
                      <img
                        src={image}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            image_url: prev.image_url.filter((_, i) => i !== index),
                          }));
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity transform hover:scale-110"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-900">
            Operating Days <span className="text-red-500">*</span>
          </h3>
          <button
            type="button"
            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${selectedDays.length === 7
              ? "bg-red-100 text-red-700 hover:bg-red-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            onClick={() => {
              if (selectedDays.length === 7) setSelectedDays([]);
              else setSelectedDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]);
            }}
          >
            {selectedDays.length === 7 ? "Unselect All" : "Select All"}
          </button>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 mb-4">
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
            <button
              type="button"
              key={day}
              className={`py-1.5 px-1 rounded-lg text-[10px] sm:text-xs font-bold uppercase transition-all ${selectedDays.includes(day)
                ? "bg-[#067978] text-white shadow-sm"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              onClick={() => handleDayToggle(day)}
            >
              {day.slice(0, 3)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-800 ml-1">Opening Time <span className="text-red-500">*</span></label>
            <input
              type="time"
              name="openingTime"
              className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
              value={formData.openingTime}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-800 ml-1">Closing Time <span className="text-red-500">*</span></label>
            <input
              type="time"
              name="closingTime"
              className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
              value={formData.closingTime}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          {['bank_name', 'branch_name', 'account_number', 'ifsc_code'].map(field => (
            <div key={field} className="space-y-1">
              <label className="text-xs font-semibold text-gray-800 capitalize ml-1">{field.replace('_', ' ')}</label>
              <input
                type="text"
                name={field}
                className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978]"
                value={formData[field]}
                onChange={handleInputChange}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="space-y-3.5 animate-fade-in-up">
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-800 ml-1">
          Create Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978] pr-10"
            value={formData.password}
            onChange={handleInputChange}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-3 text-gray-400 hover:text-[#067978]"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-semibold text-gray-800 ml-1">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#067978] pr-10"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-3 text-gray-400 hover:text-[#067978]"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector(".location-dropdown");
      if (
        isLocationDropdownOpen &&
        dropdown &&
        !dropdown.contains(event.target)
      ) {
        setIsLocationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLocationDropdownOpen]);

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden">
      {/* FIXED Header Title */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <h2 className="text-xl font-bold text-gray-900">
          {currentStep === 0 ? "Get Started" : "Partner Details"}
        </h2>
        {isPhoneVerified && (
          <div className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg> Verified
          </div>
        )}
      </div>

      {/* SCROLLABLE Middle Form Fields Only */}
      <div className="flex-1 overflow-y-auto min-h-0 pr-1 space-y-3 scroll-custom">
        {currentStep === 0 && renderPhoneVerificationStep()}
        {currentStep === 1 && renderStepOne()}
        {currentStep === 2 && renderStepTwo()}
        {currentStep === 3 && renderStepThree()}
      </div>

      {/* FIXED Bottom Action Buttons & Links */}
      {currentStep > 0 && (
        <div className="shrink-0 pt-3 mt-2 border-t border-gray-100 bg-white">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleBack}
              className="w-full border-2 border-[#067978]/20 text-[#067978] font-bold rounded-xl hover:bg-[#067978]/5 transition-all text-sm py-2.5 bg-white/50 cursor-pointer order-2 sm:order-1"
            >
              Back
            </button>
            <button
              onClick={handleContinue}
              className="w-full py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-sm rounded-xl shadow-lg transition-all cursor-pointer order-1 sm:order-2"
            >
              {currentStep === 3 ? "Join as a Partner" : "Continue"}
            </button>
          </div>

          <p className="text-center mt-2.5 text-xs text-gray-600 font-medium">
            Have an account already?{" "}
            <span
              onClick={() => navigate("/login")}
              className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
            >
              Login
            </span>
          </p>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default PartnerRegistration;